import React from "react";
import {
  Shield,
  Eye,
  Lock,
  Users,
  Database,
  Globe,
  Mail,
  Calendar,
  Baby,
  FileText,
  Settings,
} from "lucide-react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import Meta from "../components/Meta";

export default function PrivacyPolicyPage() {
  return (
    <>
      <Meta
        title="Privacy Policy - Govind Sinha & Co."
        description="At Govind Sinha and Co., accessible from https://cagovindsinha.com/, one of our main priorities is the privacy of our visitors. This Privacy Policy document contains types of information that is collected and recorded by Govind Sinha and Co. and how we use it."
        canonical="https://cagovindsinha.com/privacy-policy"
      />
      <div className="min-h-screen pt-16 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <Navbar />
        <div className="max-w-4xl px-6 py-12 mx-auto">
          {/* Single Content Card */}
          <div className="p-8 transition-all duration-300 border shadow-xl bg-white/80 backdrop-blur-sm rounded-2xl border-slate-200/50 hover:shadow-2xl">
            {/* Introduction */}
            <div className="flex items-start mb-8 space-x-4">
              <div className="p-3 bg-indigo-100 rounded-full">
                <Shield className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="mb-4 text-3xl font-bold text-transparent bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text">
                  Privacy Policy
                </h1>
                <p className="mb-4 leading-relaxed text-slate-600">
                  At Govind Sinha and Co., accessible from
                  https://cagovindsinha.com/, one of our main priorities is the
                  privacy of our visitors. This Privacy Policy document contains
                  types of information that is collected and recorded by Govind
                  Sinha and Co. and how we use it.
                </p>
                <p className="mb-4 leading-relaxed text-slate-600">
                  If you have additional questions or require more information
                  about our Privacy Policy, do not hesitate to contact us.
                </p>
                <p className="leading-relaxed text-slate-600">
                  This Privacy Policy applies only to our online activities and
                  is valid for visitors to our website with regards to the
                  information that they shared and/or collect in Govind Sinha
                  and Co. This policy is not applicable to any information
                  collected offline or via channels other than this website.
                </p>
                <div className="flex items-center mt-4 space-x-4 text-sm text-slate-500">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>Last updated: July 2025</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Globe className="w-4 h-4" />
                    <span>Effective globally</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-slate-200">
              {/* Consent */}
              <div className="mb-8">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Settings className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="mb-3 text-lg font-semibold text-slate-800">
                      Consent
                    </h3>
                    <p className="leading-relaxed text-slate-600">
                      By using our website, you hereby consent to our Privacy
                      Policy and agree to its terms.
                    </p>
                  </div>
                </div>
              </div>

              {/* Information We Collect */}
              <div className="mb-8">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Database className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="mb-3 text-lg font-semibold text-slate-800">
                      Information We Collect
                    </h3>
                    <div className="space-y-4 leading-relaxed text-slate-600">
                      <p>
                        The personal information that you are asked to provide,
                        and the reasons why you are asked to provide it, will be
                        made clear to you at the point we ask you to provide
                        your personal information.
                      </p>
                      <p>
                        If you contact us directly, we may receive additional
                        information about you such as your name, email address,
                        phone number, the contents of the message and/or
                        attachments you may send us, and any other information
                        you may choose to provide.
                      </p>
                      <p>
                        When you register for an Account, we may ask for your
                        contact information, including items such as name,
                        company name, address, email address, and telephone
                        number.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* How We Use Your Information */}
              <div className="mb-8">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Eye className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="mb-3 text-lg font-semibold text-slate-800">
                      How We Use Your Information
                    </h3>
                    <p className="mb-3 leading-relaxed text-slate-600">
                      We use the information we collect in various ways,
                      including to:
                    </p>
                    <ul className="ml-4 space-y-2 leading-relaxed text-slate-600">
                      <li>• Provide, operate, and maintain our website</li>
                      <li>• Improve, personalize, and expand our website</li>
                      <li>• Understand and analyze how you use our website</li>
                      <li>
                        • Develop new products, services, features, and
                        functionality
                      </li>
                      <li>
                        • Communicate with you, either directly or through one
                        of our partners, including for customer service, to
                        provide you with updates and other information relating
                        to the website, and for marketing and promotional
                        purposes
                      </li>
                      <li>• Send you emails</li>
                      <li>• Find and prevent fraud</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Log Files */}
              <div className="mb-8">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <FileText className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="mb-3 text-lg font-semibold text-slate-800">
                      Log Files
                    </h3>
                    <p className="leading-relaxed text-slate-600">
                      Govind Sinha and Co. follows a standard procedure of using
                      log files. These files log visitors when they visit
                      websites. All hosting companies do this and a part of
                      hosting services' analytics. The information collected by
                      log files include internet protocol (IP) addresses,
                      browser type, Internet Service Provider (ISP), date and
                      time stamp, referring/exit pages, and possibly the number
                      of clicks. These are not linked to any information that is
                      personally identifiable. The purpose of the information is
                      for analyzing trends, administering the site, tracking
                      users' movement on the website, and gathering demographic
                      information.
                    </p>
                  </div>
                </div>
              </div>

              {/* Analytics and Tracking */}
              <div className="mb-8">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-pink-100 rounded-lg">
                    <Globe className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <h3 className="mb-3 text-lg font-semibold text-slate-800">
                      Analytics and Tracking
                    </h3>
                    <div className="space-y-4 leading-relaxed text-slate-600">
                      <p>
                        We use PostHog, a third-party analytics service, to help
                        us understand how users interact with our website.
                        PostHog collects information about your usage patterns,
                        including pages visited, time spent on pages, and user
                        interactions.
                      </p>
                      <p>
                        PostHog may use cookies and similar tracking
                        technologies to collect this information. The data
                        collected is used to analyze website performance,
                        improve user experience, and understand user behavior
                        patterns. This information is anonymized and aggregated.
                      </p>
                      <p>
                        For more information about PostHog's data practices,
                        please review their privacy policy at
                        https://posthog.com/privacy. You can opt out of PostHog
                        tracking by adjusting your browser settings or using
                        their opt-out mechanisms.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Third Party Services */}
              <div className="mb-8">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <Users className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="mb-3 text-lg font-semibold text-slate-800">
                      Third Party Services
                    </h3>
                    <div className="space-y-4 leading-relaxed text-slate-600">
                      <p>
                        Our website may integrate with third-party services to
                        provide enhanced functionality. These services have
                        their own privacy policies and terms of service that
                        govern how they collect and use your information.
                      </p>
                      <p>
                        You can choose to disable cookies through your
                        individual browser options. To know more detailed
                        information about cookie management with specific web
                        browsers, it can be found at the browsers' respective
                        websites.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CCPA Privacy Rights */}
              <div className="mb-8">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Lock className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="mb-3 text-lg font-semibold text-slate-800">
                      CCPA Privacy Rights (Do Not Sell My Personal Information)
                    </h3>
                    <div className="space-y-4 leading-relaxed text-slate-600">
                      <p>
                        Under the CCPA, among other rights, California consumers
                        have the right to:
                      </p>
                      <ul className="ml-4 space-y-2">
                        <li>
                          • Request that a business that collects a consumer's
                          personal data disclose the categories and specific
                          pieces of personal data that a business has collected
                          about consumers.
                        </li>
                        <li>
                          • Request that a business delete any personal data
                          about the consumer that a business has collected.
                        </li>
                        <li>
                          • Request that a business that sells a consumer's
                          personal data, not sell the consumer's personal data.
                        </li>
                      </ul>
                      <p>
                        If you make a request, we have one month to respond to
                        you. If you would like to exercise any of these rights,
                        please contact us.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* GDPR Data Protection Rights */}
              <div className="mb-8">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Shield className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="mb-3 text-lg font-semibold text-slate-800">
                      GDPR Data Protection Rights
                    </h3>
                    <div className="space-y-4 leading-relaxed text-slate-600">
                      <p>
                        We would like to make sure you are fully aware of all of
                        your data protection rights. Every user is entitled to
                        the following:
                      </p>
                      <ul className="ml-4 space-y-2">
                        <li>
                          • <strong>The right to access</strong> – You have the
                          right to request copies of your personal data. We may
                          charge you a small fee for this service.
                        </li>
                        <li>
                          • <strong>The right to rectification</strong> – You
                          have the right to request that we correct any
                          information you believe is inaccurate. You also have
                          the right to request that we complete the information
                          you believe is incomplete.
                        </li>
                        <li>
                          • <strong>The right to erasure</strong> – You have the
                          right to request that we erase your personal data,
                          under certain conditions.
                        </li>
                        <li>
                          • <strong>The right to restrict processing</strong> –
                          You have the right to request that we restrict the
                          processing of your personal data, under certain
                          conditions.
                        </li>
                        <li>
                          • <strong>The right to object to processing</strong> –
                          You have the right to object to our processing of your
                          personal data, under certain conditions.
                        </li>
                        <li>
                          • <strong>The right to data portability</strong> – You
                          have the right to request that we transfer the data
                          that we have collected to another organization, or
                          directly to you, under certain conditions.
                        </li>
                      </ul>
                      <p>
                        If you make a request, we have one month to respond to
                        you. If you would like to exercise any of these rights,
                        please contact us.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Children's Information */}
              <div className="mb-8">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Baby className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="mb-3 text-lg font-semibold text-slate-800">
                      Children's Information
                    </h3>
                    <div className="space-y-4 leading-relaxed text-slate-600">
                      <p>
                        Another part of our priority is adding protection for
                        children while using the internet. We encourage parents
                        and guardians to observe, participate in, and/or monitor
                        and guide their online activity.
                      </p>
                      <p>
                        Govind Sinha and Co. does not knowingly collect any
                        Personal Identifiable Information from children under
                        the age of 13. If you think that your child provided
                        this kind of information on our website, we strongly
                        encourage you to contact us immediately and we will do
                        our best efforts to promptly remove such information
                        from our records.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-slate-500">
            <p className="text-sm">
              This privacy policy is part of our commitment to transparency and
              your digital rights.
            </p>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
